import type { ReactElement } from 'react';
import { memo, useCallback, useMemo } from 'react';

import type { FilterOption } from '../../../types/FilterOption';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { useGlobalState } from '../../../../../global_state/src';

import { stylesFilterResultsLimit } from './constants';

/** Props for the shared styles filter options header. */
interface StylesFilterOptionsHeaderProps {
    totalCount: number;
    optionsCount: number;
    isSearching: boolean;
    /** The styles currently selected in the filter. */
    selectedStyles: Array<FilterOption>;
    insertAllStyles: () => Promise<Array<FilterOption>>;
    /** Called with the merged selection after "Insert all results". */
    onStylesChange: (styles: Array<FilterOption>) => void;
}

/** Displays styles result summary and allows inserting all current style results. */
const StylesFilterOptionsHeader = memo(
    ({
        totalCount,
        optionsCount,
        isSearching,
        selectedStyles,
        insertAllStyles,
        onStylesChange: handleStylesChange,
    }: StylesFilterOptionsHeaderProps): ReactElement => {
        const { palette } = useTheme();
        const [, dispatchSnackbar] = useGlobalState('snackbarQueue');

        const message = useMemo((): string => {
            if (totalCount > stylesFilterResultsLimit) {
                return `Showing ${optionsCount} of ${totalCount} results`;
            }
            if (totalCount === 1) return 'Showing 1 result';
            return `Showing all ${totalCount} results`;
        }, [optionsCount, totalCount]);

        const areAllResultsSelected =
            totalCount > 0 &&
            totalCount <= stylesFilterResultsLimit &&
            selectedStyles.length >= totalCount &&
            !isSearching;

        const disabledInsertAllButtonReason = useMemo(():
            | string
            | undefined => {
            if (totalCount === 0 && !isSearching) return 'No results';
            if (areAllResultsSelected)
                return 'All results are already selected';
            return undefined;
        }, [areAllResultsSelected, isSearching, totalCount]);

        const handleInsertAll = useCallback((): void => {
            insertAllStyles()
                .then((allStyles) => {
                    const selectedValues = new Set(
                        selectedStyles.map((option) => option.value)
                    );

                    handleStylesChange([
                        ...selectedStyles,
                        ...allStyles.filter(
                            (style) => !selectedValues.has(style.value)
                        ),
                    ]);
                })
                .catch((stylesError: unknown) => {
                    const stylesErrorMessage =
                        stylesError instanceof Error
                            ? stylesError.message
                            : 'Unknown error occurred';

                    dispatchSnackbar({
                        type: 'add',
                        payload: {
                            title: 'Something went wrong loading styles',
                            message: stylesErrorMessage,
                            type: 'error',
                        },
                    });

                    console.error(stylesError);
                });
        }, [
            dispatchSnackbar,
            handleStylesChange,
            insertAllStyles,
            selectedStyles,
        ]);

        return (
            <Stack
                direction="row"
                spacing={4}
                sx={{
                    alignItems: 'center',
                    borderTop: `1px solid ${palette.divider}`,
                    justifyContent: 'space-between',
                    paddingX: 3,
                    paddingY: 4,
                    ['& + .MuiAutocomplete-noOptions']: { display: 'none' },
                    ['& + .MuiAutocomplete-listbox, & + .MuiAutocomplete-loading']:
                        {
                            borderTop: `1px solid ${palette.divider}`,
                        },
                }}
                useFlexGap
            >
                <Typography variant="caption">{message}</Typography>

                <Tooltip
                    title={disabledInsertAllButtonReason}
                    disableFocusListener={!disabledInsertAllButtonReason}
                    disableTouchListener={!disabledInsertAllButtonReason}
                    disableHoverListener={!disabledInsertAllButtonReason}
                >
                    <span>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={
                                disabledInsertAllButtonReason !== undefined
                            }
                            onClick={(event): void => {
                                event.stopPropagation();
                                handleInsertAll();
                            }}
                        >
                            Insert all results
                        </Button>
                    </span>
                </Tooltip>
            </Stack>
        );
    }
);

StylesFilterOptionsHeader.displayName = 'StylesFilterOptionsHeader';

export { StylesFilterOptionsHeader };
export type { StylesFilterOptionsHeaderProps };
